import Api from '@/services/http'
import i18n from '@/services/i18n'
import socket from '@/services/socket'
import { notificationUrl } from '@/services/url'
import store, { useSelector } from '@/store'
import { setGuideInfo } from '@/store/commonSlice'
import type { IGuideInfo } from '@/store/commonSlice/types'
import { notificationActions } from '@/store/notificationSlice'
import type { ITaskResultProps, ResponseUnReadType } from '@/store/notificationSlice/type'
import { updateWalletListInfo } from '@/store/userSlice'
import type { IWallInfo } from '@/store/userSlice/types'
import { OneMessage } from '@yy/one-ui'
import Cookies from 'js-cookie'
import { useEffect, useRef, useState } from 'react'

/** 异步任务消息事件 */
enum MessageTypeEnum {
  /** 用户消息 */
  USER = 1,
  /** 首页通知 */
  DASHBOARD = 2,
}

export interface AwaitAsyncTaskOptions {
  timeout?: number
  /** 任务失败时是否toast出来 */
  errorToast?: boolean
  /** 任务失败时是否抛错出来 */
  throwError?: boolean
  taskType?: string
  /** 传入的id类型 */
  idType?: 'nonce' | 'taskId'
  /** 超时是否抛出错误 */
  throwTimeoutError?: boolean
}
/**
 * 等待某个异步任务推送
 * 如果没有配置throwTimeoutError，超时会返回null，请一定要处理超时错误
 * @param id
 * @returns
 */
export async function awaitAsyncTask(id: string, timeout?: number): Promise<ITaskResultProps>
export async function awaitAsyncTask(id: string, options?: AwaitAsyncTaskOptions): Promise<ITaskResultProps>
export async function awaitAsyncTask(id: string, timeoutOrOptions: AwaitAsyncTaskOptions | number = { timeout: 60 * 1000 }) {
  let unsubscribe = null
  const options = typeof timeoutOrOptions === 'number' ? { timeout: timeoutOrOptions } : timeoutOrOptions
  const check = () => {
    const taskResultsMapAll = store.getState().notification.taskResultsMapAll
    const task = taskResultsMapAll.find((t) => {
      /** nonce is the temporaily key for the sepcial topics */
      const msgId = options.idType === 'nonce' ? t.nonce : t.taskId
      if (options.taskType) return id === msgId && t.taskType === options.taskType
      return id === msgId
    })

    // 处理错误
    if (options.errorToast && task && !task.response?.success) {
      const key = `system.error.${task.response?.code}`
      const parametes = { ...task.response?.extMap }
      const i18nText = i18n.t(key, parametes)
      const message = key === i18nText ? task.response?.message : i18nText
      OneMessage.error(message)
    }
    return task
  }
  // 先检查一下taskId是否已经存在
  const task = check()
  if (task) {
    if (!task.response?.success && options.throwError) Promise.reject(task)
    else Promise.resolve(task)
  }

  // 如果没有，则等待通知
  const taskPromise = new Promise<ITaskResultProps>((resolve, reject) => {
    unsubscribe = store.subscribe(() => {
      const task = check()
      if (!task) return
      unsubscribe()
      if (!task.response?.success && options.throwError) reject(task)
      else resolve(task)
    })
  })

  const timeoutPromise = new Promise<null>((resolve, reject) => {
    setTimeout(() => {
      unsubscribe && unsubscribe()
      if (options.throwTimeoutError) reject(new Error('timeout'))
      else resolve(null)
    }, options.timeout)
  })

  let res = null
  try {
    res = await Promise.race([taskPromise, timeoutPromise])
  } catch (error) {
    if (error.message === 'timeout' && options.errorToast) OneMessage.error(i18n.t('system.error.2'))
    throw error
  }

  return res
}

/** 更新异步消息, 通知到对应业务组件 */
const updateAsyncTask = (task: ITaskResultProps) => {
  const { taskId } = task
  store.dispatch(notificationActions.addTaskResultsItem(task))
  store.dispatch(notificationActions.deleteTaskId({ taskId }))

  // 触发通知到页面后就自动把对应的task移除
  setTimeout(() => {
    store.dispatch(notificationActions.deleteTaskResultsItem({ taskId }))
  }, 60)
}

/** 未读通知消息 */
export const queryUnRead = async () => {
  const states = store.getState()
  const params = {
    appId: process.env.WEBSOCKET_APP_ID,
    taskIds: states.notification.taskIdCaches,
  }

  const response = await Api.admin.post<ResponseUnReadType>(notificationUrl.queryUnRead, params)
  if (response) {
    const { data, bizCode, bizMessage } = response
    if (bizCode === 'SUCCESS') {
      const { messageCount, exportCount, taskResults } = data
      store.dispatch(notificationActions.setUnReadMessageCount({ messageCount }))
      store.dispatch(notificationActions.setUnReadDownloadCount({ exportCount }))

      taskResults?.forEach((task) => {
        updateAsyncTask(task)
      })

      return data
    }

    return Promise.reject(bizMessage)
  }
}

export const updateBalance = (data: IWallInfo) => {
  store.dispatch(updateWalletListInfo(data))
}

export const updateGuideInfo = (data: IGuideInfo) => {
  const process = data?.taskDetailResBos?.filter((i) => i.completeStatus).length
  store.dispatch(
    setGuideInfo({
      ...data,
      process,
    })
  )
}

/** socket 配置与初始化 */
export const useSocket = () => {
  const { isLogined, info } = useSelector((state) => state.user)
  const uid = info?.uid
  const isInit = useRef(false)

  // 用户消息
  useEffect(() => {
    return socket.onUserMessage((payload) => {
      // 后端没法保证用户消息都有messageType字段，兼容设置默认值1
      const { messageCount, userMessage, messageType = 1 } = payload
      switch (messageType) {
        case MessageTypeEnum.USER:
          store.dispatch(notificationActions.setUnReadMessageCount({ messageCount }))
          store.dispatch(notificationActions.setSocketMessageList([userMessage]))
          break
        case MessageTypeEnum.DASHBOARD:
          store.dispatch(notificationActions.setHasNotifyCount(true))
          break
        default:
          break
      }
    })
  }, [])

  // 导出任务消息
  useEffect(() => {
    return socket.onExportListMessage((payload) => {
      const { exportCount, exportMessage } = payload
      store.dispatch(notificationActions.setUnReadDownloadCount({ exportCount }))
      store.dispatch(notificationActions.setSocketDownloadList([exportMessage]))
    })
  }, [])

  // 异步任务消息
  useEffect(() => {
    return socket.onAsyncResult(updateAsyncTask)
  }, [])

  // 断开连接，重新获取未读消息
  useEffect(() => {
    return socket.onClosed(queryUnRead)
  }, [])

  // 监听余额更新
  useEffect(() => {
    return socket.onBalanceUpdate(updateBalance)
  }, [])

  // 监听新手指引更新
  useEffect(() => {
    return socket.onGuideInfoUpdate(updateGuideInfo)
  }, [])

  // 获取未读信息
  useEffect(() => {
    if (isLogined) {
      queryUnRead()
    }
  }, [isLogined])

  useEffect(() => {
    const udbSubAppId = Cookies.get('osudb_subappid')
    if (uid && udbSubAppId && !isInit.current) {
      isInit.current = true

      // 配置
      socket.configure(() => ({
        env: process.env.APP_ENV.toUpperCase(),
        /**
         * @todo
         * 此处根据打包环境，不能根据用户真正所在地区进行就近 websocket 连接
         * 后面可以根据服务器环境确认 socket 连接地址
         */
        wsDomain: process.env.WS_ENV === 'nation' ? process.env.WEBSOCKET_URL_NATION : process.env.WEBSOCKET_URL,
        appId: process.env.WEBSOCKET_APP_ID,
        privateKey: process.env.WEBSOCKET_PRIVATE_KEY,
        broadcasts: typeof process.env.WEBSOCKET_UDB_BOARDCASTS === 'string' ? process.env.WEBSOCKET_UDB_BOARDCASTS.split(',') : [],
        udbAuthUrl: `${window.location.origin}/udb/lgn/sdk/getCookieOtp.do`,
        udbAppId: process.env.WEBSOCKET_UDB_APP_ID,
        udbSubAppId,
        uid,
      }))

      socket.open()
    }

    return () => {
      if (isInit.current) {
        socket?.close()
      }
    }
  }, [uid])
}

/** socket 版本更新 */
export const useVersionUpdate = () => {
  const [needUpdate, setNeedUpdate] = useState(false)
  const myuid = useSelector((state) => state.user.info?.uid)

  useEffect(() => {
    return socket.onVersionUpdate((payload) => {
      const { version, uid } = payload
      const users = typeof uid === 'string' && uid.length > 0 ? uid.split(',') : []

      // 如果用户为0 或者 UID 为空字符串均当做所有用户启用
      if (users.length > 0) {
        if (users.includes(myuid) && version !== process.env.GIT_COMMIT_HASH) {
          setNeedUpdate(true)
        }
      } else if (version !== process.env.GIT_COMMIT_HASH) {
        setNeedUpdate(true)
      }
    })
  }, [myuid])

  return needUpdate
}
